import TextFontUpdater from 'client/components/core/Sidebar/components/Settings/components/Font/TextFontUpdater'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import React from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { ResetEntityStyles } from 'src/editor/shared/settings/reset-entity-styles-button'
import { SalesRedirectionButtonEntityInterface } from 'src/shared/entities/sales-redirection-button/sales-redirection-button-entity.schema'
import ButtonIconPicker from '../../../components/core/Sidebar/components/Settings/components/ButtonIconPicker'
import HoverButtonSetting from '../../../components/core/Sidebar/components/Settings/components/HoverButton'
import Input from '../../../components/core/Sidebar/components/Settings/components/Input'
import { SettingsProps } from '../../../components/core/Sidebar/components/Settings/entities'
import {
  useUpdateEntity,
  useUpdateProp,
} from '../../../components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import {
  LineHeight,
  ShadowStyle,
} from '../../../components/core/Sidebar/components/Settings/styles'
import Border from '../../../components/core/Sidebar/components/Settings/styles/Border'
import ButtonWidth from '../../../components/core/Sidebar/components/Settings/styles/ButtonWidth'
import FlexAlignStyle from '../../../components/core/Sidebar/components/Settings/styles/FlexAlignStyle'
import FontSize from '../../../components/core/Sidebar/components/Settings/styles/FontSize'
import { useDeviceModeContext } from '../../../context/DeviceModeContext'

function SalesRedirectionButtonSettings({
  entity,
  defaultEntity,
}: SettingsProps<SalesRedirectionButtonEntityInterface>) {
  const updateProp = useUpdateProp(entity)
  const { isMobile } = useDeviceModeContext()
  const updateEntity = useUpdateEntity<SalesRedirectionButtonEntityInterface>()

  return (
    <>
      <FlexAlignStyle
        align={entity.alignSelf}
        mobileAlign={entity.mobileAlignSelf}
        update={updateProp('alignSelf')}
        mobileUpdate={updateProp('mobileAlignSelf')}
      />
      <ButtonWidth
        update={updateProp('width')}
        mobileUpdate={updateProp('mobileWidth')}
        width={entity.width}
        mobileWidth={entity.mobileWidth}
      />
      <ColorPickerControl
        isGradientAvailable
        update={updateProp('background')}
        mobileUpdate={updateProp('mobileBackground')}
        color={entity.background}
        mobileColor={entity.mobileBackground}
        label="settings_styles.background_color.label"
      />
      <ButtonIconPicker
        valueBefore={entity.iconClassNameBefore}
        valueAfter={entity.iconClassNameAfter}
        updateBefore={updateProp('iconClassNameBefore')}
        updateAfter={updateProp('iconClassNameAfter')}
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                'textFontSize',
                'textFontFamily',
                'textFontFileId',
                'textFontStyle',
                'textFontWeight',
                'textColor',
              ]}
              mobileKeys={[
                'mobileTextFontSize',
                'mobileTextFontFamily',
                'mobileFontFileId',
                'mobileTextFontStyle',
                'mobileTextFontWeight',
                'mobileTextColor',
              ]}
            />
          )
        }
      >
        entity_settings.button.text.group_title
      </GroupTitle>
      <Input
        labelText="entity_settings.button.text.label"
        value={entity.text}
        update={updateProp('text')}
        placeholder=""
        type="text"
        helpMessage=""
      />
      <FontSize
        update={updateProp('textFontSize')}
        mobileUpdate={updateProp('mobileTextFontSize')}
        fontSize={entity.textFontSize}
        mobileFontSize={entity.mobileTextFontSize}
      />
      <LineHeight
        lineHeight={entity.lineHeight}
        mobileLineHeight={entity.mobileLineHeight}
        update={updateProp('lineHeight')}
        mobileUpdate={updateProp('mobileLineHeight')}
        fontSize={entity.textFontSize}
        mobileFontSize={entity.mobileTextFontSize}
      />
      <TextFontUpdater<SalesRedirectionButtonEntityInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'textFontFamily'}
        fontWeightKey={'textFontWeight'}
        fontStyleKey={'textFontStyle'}
        mobileFontFamilyKey={'mobileTextFontFamily'}
        mobileFontWeightKey={'mobileTextFontWeight'}
        mobileFontStyleKey={'mobileTextFontStyle'}
        fontFileIdKey={'textFontFileId'}
        mobileFontFileIdKey={'mobileTextFontFileId'}
      />
      <ColorPickerControl
        update={updateProp('textColor')}
        mobileUpdate={updateProp('mobileTextColor')}
        color={entity.textColor}
        mobileColor={entity.mobileTextColor}
      />
      <GroupTitle
        afterChildren={
          defaultEntity && (
            <ResetEntityStyles
              entity={entity}
              defaultEntity={defaultEntity}
              keys={[
                'subTextFontSize',
                'subTextFontFamily',
                'subTextFontFileId',
                'subTextFontStyle',
                'subTextFontWeight',
                'subTextColor',
              ]}
              mobileKeys={[
                'mobileSubTextFontSize',
                'mobileSubTextFontFamily',
                'mobileSubTextFontFileId',
                'mobileSubTextFontStyle',
                'mobileSubTextFontWeight',
                'mobileSubTextColor',
              ]}
            />
          )
        }
      >
        entity_settings.button.sub_text.group_title
      </GroupTitle>
      <Input
        labelText="entity_settings.button.sub_text.label"
        value={entity.subText}
        update={updateProp('subText')}
        placeholder=""
        type="text"
        helpMessage=""
      />
      <FontSize
        update={updateProp('subTextFontSize')}
        mobileUpdate={updateProp('mobileSubTextFontSize')}
        fontSize={entity.subTextFontSize}
        mobileFontSize={entity.mobileSubTextFontSize}
      />
      <TextFontUpdater<SalesRedirectionButtonEntityInterface>
        entity={entity}
        update={updateEntity}
        fontFamilyKey={'subTextFontFamily'}
        fontWeightKey={'subTextFontWeight'}
        fontStyleKey={'subTextFontStyle'}
        mobileFontFamilyKey={'mobileSubTextFontFamily'}
        mobileFontWeightKey={'mobileSubTextFontWeight'}
        mobileFontStyleKey={'mobileSubTextFontStyle'}
        fontFileIdKey={'subTextFontFileId'}
        mobileFontFileIdKey={'mobileSubTextFontFileId'}
      />
      <ColorPickerControl
        update={updateProp('subTextColor')}
        mobileUpdate={updateProp('subTextColor')}
        color={entity.subTextColor}
        mobileColor={entity.mobileSubTextColor}
        isClearable
      />
      <HoverButtonSetting
        update={updateProp('hover')}
        mobileUpdate={updateProp('mobileHover')}
        hover={entity?.hover}
        defaultHover={defaultEntity?.hover}
        mobileDefaultHover={defaultEntity?.mobileHover}
        mobileHover={entity?.mobileHover}
        backgroundColor={entity.background ?? entity?.backgroundColor}
        textColor={entity?.textColor}
        subTextColor={entity?.subTextColor}
      />
      <Border
        update={updateProp('border')}
        mobileUpdate={updateProp('mobileBorder')}
        border={entity.border}
        mobileBorder={entity.mobileBorder}
        defaultBorder={defaultEntity?.border}
        mobileDefaultBorder={defaultEntity?.mobileBorder}
      />
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        defaultPadding={
          isMobile ? defaultEntity?.mobilePadding : defaultEntity?.padding
        }
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
        margin={isMobile ? entity.mobileMargin : entity.margin}
        defaultMargin={
          isMobile ? defaultEntity?.mobileMargin : defaultEntity?.margin
        }
        updateMargin={updateProp(isMobile ? 'mobileMargin' : 'margin')}
      />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}

export default SalesRedirectionButtonSettings
